import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fuel, Clock } from "lucide-react";
import { api } from "@/lib/api";

import { useTranslation } from "react-i18next";
/**
 * Sticky shift-status pill for CSA — floats at the top of every screen while
 * the CSA is checked in. Shows current shift + elapsed time + nozzles picked.
 *
 * Hidden entirely when there's no active check-in (no visual noise off-shift).
 */
export default function ShiftStatusPill({ user }) {
  const { t: __t } = useTranslation();
  const [state, setState] = useState(null);
  const [elapsed, setElapsed] = useState("");

  useEffect(() => {
    if (!user || user.role !== "CSA") return;
    let cancelled = false;
    const load = async () => {
      try {
        const [{ data: att }, { data: allocs }] = await Promise.all([
          api.get("/attendance/active").catch(() => ({ data: null })),
          api.get("/nozzles/allocations/mine").catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        if (att?.attendance) {
          setState({
            checkedInAt: att.attendance.checked_in_at,
            shiftId: att.attendance.shift_id,
            nozzles: (allocs.items || []).length,
          });
        } else {
          setState(null);
        }
      } catch {
        // silent
      }
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  useEffect(() => {
    if (!state?.checkedInAt) return;
    const tick = () => {
      const diff = Date.now() - new Date(state.checkedInAt).getTime();
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      setElapsed(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const t = setInterval(tick, 15_000);
    return () => clearInterval(t);
  }, [state]);

  if (!state) return null;

  return (
    <Link
      to="/csa/shift"
      className="fixed bottom-4 left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 rounded-full border border-emerald-400 bg-emerald-500/95 px-3 py-1.5 text-[11px] font-semibold text-white shadow-lg backdrop-blur-sm hover:bg-emerald-600"
      data-testid="shift-status-pill"
    >
      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
      <Fuel className="h-3.5 w-3.5" />
      <span>{__t("components.shiftstatuspill.shift_active")}</span>
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-700/60 px-1.5 py-0.5">
        <Clock className="h-3 w-3" /> {elapsed}
      </span>
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-700/60 px-1.5 py-0.5">
        {state.nozzles} nozzle{state.nozzles !== 1 ? "s" : ""}
      </span>
    </Link>
  );
}
